import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { intercept, observe } from 'mobx';
import { inject, observer } from 'mobx-react';
import * as moment from 'moment-timezone';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { Button, ButtonGroup } from 'reactstrap';
import TenantStore from 'src/Stores/Tenant/TenantStore';
import { TestsStore } from 'src/Stores/Tests/TestsStore';
import { UserStore } from 'src/Stores/User/UserStore';
import { WellsStore } from 'src/Stores/Wells/WellsStore';
import { ITest } from '../../../Interfaces/TestInterface';
import { IUser } from '../../../Interfaces/UserInterface';
import { IWell } from '../../../Interfaces/WellInterface';
import DataService from '../../../Services/Data';
import DataTable from '../../DataTable';
import ToolTip from '../../Tooltip';
import './WellList.scss';





interface IList {
    headers: any[];
    rows: any[];
}


@inject('stores')
@observer
class WellList extends React.Component {
    public props: any;
    private tests: ITest[] = [];
    public testDisposer: any;
    public TestsStore: TestsStore;
    public user: IUser;
    public UserStore: UserStore;
    public userDisposer: any;
    public wells: IWell[] = [];
    public WellsStore: WellsStore;
    private TenantStore: TenantStore;
    public wellListDisposer: any;
    public wellList: IList;
    public roles: any[];
    public dataService = new DataService();

    public state = {
        loading: true,
        newWellAvail: false,
        showAll: false,
        statusFilters: ['Active'],
        testList: this.tests,
        user: this.user,
        wellList: this.wellList,
        wells: this.wells,
    };

    constructor(props: any) {
        super(props);
        this.UserStore = this.props.stores.UserStore;
        this.WellsStore = this.props.stores.WellsStore;
        this.TestsStore = this.props.stores.TestsStore;
        this.TenantStore = this.props.stores.TenantStore;
        this.state.newWellAvail = this.UserStore.hasPermission('Permissions.Wells.Create');
    }


    public componentDidMount() {
        this.userDisposer = observe(this.UserStore, 'user', (change: any) => {
            if (change.newValue) {
                const statusFilters = this.getFiltersFromPreferences(change.newValue.preferences);
                this.setState({
                    newWellAvail: this.UserStore.hasPermission('Permissions.Wells.Create'),
                    showAll: change.newValue.preferences['Preferences.Wells.Assignment'] === 'All' ? true : false,
                    statusFilters,
                    user: change.newValue,
                }, () => this.formatWellListObject());
            }
            
            return change;
        });

        this.wellListDisposer = intercept(this.WellsStore, 'wells', (change: any) => {
            this.setState({
                loading: false,
                wells: change.newValue || []
            }, () => this.formatWellListObject());

            return change;
        });
        this.WellsStore.getWells();
        this.WellsStore.clearWell();
        this.WellsStore.clearWellCloseReview();
        this.UserStore.getProfile();
        this.TestsStore.clearTestStore();
    }

    public componentWillUnmount() {
        this.userDisposer();
        this.wellListDisposer();
    }

    private getFiltersFromPreferences = (preferences: any) => {
        let statusFilters = ['Active'];
        
        if (preferences === undefined || !Object.keys(preferences).some(p => p.includes('Preferences.Wells.List'))) {
            return statusFilters;
        }

        if (this.UserStore.hasGenericPermission('Permissions.Wells.ReadClosed')) {
            statusFilters = [
                preferences['Preferences.Wells.List.Active'] === 'true' ? 'Active' : '',
                preferences['Preferences.Wells.List.Finishing'] === 'true' ? 'Finishing' : '',
                preferences['Preferences.Wells.List.Closed'] === 'true' ? 'Closed' : '',
            ];
            if (this.UserStore.hasPermission('Permissions.Wells.ViewArchive')) {
                statusFilters.push(preferences['Preferences.Wells.List.Archived'] === 'true' ? 'Archived' : '');
                if (statusFilters.filter(t => t !== '').length === 4) {
                    statusFilters = [];
                }
            } else {
                if (statusFilters.filter(t => t !== '').length === 3) {
                    statusFilters = [];
                }
            }
        }
        return statusFilters.filter(t => t !== '');
        
    }

    private renderNewTestButton = (well: IWell) => () => {
        const testUrl = "/well/" + well.id + "/test/new/welldata/testsetup";
        if (well.wellStatus === 'Active') {
            return(
                <ToolTip className="icon-wrapper" text="Tests" position="right">
                    <Link to={testUrl}>
                            <FontAwesomeIcon icon="clipboard-check" color={this.TenantStore.iconColor} size="2x"/>
                    </Link>
                </ToolTip>        
            );
        }

        return '';
    }

    private renderWellFilesButton = (well: IWell) => () => {
        const printUrl = '/well/' + well.id + '/files';
        return (
        <ToolTip className="icon-wrapper" text="Well Files" position="right">
            <Link to={printUrl}>
                <FontAwesomeIcon icon="file-download" color={this.TenantStore.iconColor} size="2x"/>
            </Link>
        </ToolTip>
        );
    }

    private renderDispatchesButton = (well: IWell) => () => {
        const printUrl = '/well/' + well.id + '/dispatches';
        return (
        <ToolTip className="icon-wrapper" text="Well Dispatches" position="right">
            <Link to={printUrl}>
                <FontAwesomeIcon icon="truck" color={this.TenantStore.iconColor} size="2x"/>
            </Link>
        </ToolTip>
        );
    }

    private renderCompleteIcon = (well: any) => () => {
        const compClass = (well.wellSetupComplete) ? 'isComplete' : 'isNotComplete';
        return(
            <div className={compClass}>
                <FontAwesomeIcon icon={['far', 'check-circle']} />
                {well.wellId}
            </div>
        );
    }

    private renderWellName = (well: IWell) => () => {
        const wellUrl = "/well/" + well.id + '/details';
        return(
            <Link to={wellUrl}>{well.wellName}</Link>
        );
    }

    private renderWellOperator = (well: IWell) => () => {
        return(
            <div>{ well.operatorName && well.operatorName }</div>
        );
    }

    private renderWellSpudDate = (well: IWell) => () => {
        return(
            <div>{ well.spudDate && well.spudDate }</div>
        );
    }

    private renderWellContractor = (well: IWell) => () => {
        return(
            <div>{well.contractorName && well.contractorName}</div>
        );
    }

    private filterNotAssigned = (well: IWell) => {
        if (this.UserStore.hasPermission('Permissions.Wells.Read.Separate') && !this.state.showAll) {
            return (well.engineerIds && well.engineerIds.some(id => id === this.UserStore.user.id));
        }

        if (well.wellStatus !== 'Active' && !this.UserStore.hasPermission('Permissions.Wells.ReadClosed.All')) {
            return (well.engineerIds && well.engineerIds.some(id => id === this.UserStore.user.id));
        }

        if (this.UserStore.hasPermission('Permissions.Wells.Read.All')) {
            return true;
        }
        
        if (this.UserStore.hasPermission('Permissions.Wells.Read.Assigned')) {
            return (well.engineerIds && well.engineerIds.some(id => id === this.UserStore.user.id));
        }
        return true;
    }

    public filterList = (item: IWell) => {
        let resultType;
        if (this.state.statusFilters.length === 0) {
            resultType = true;
        } else {

            for (const filterName of this.state.statusFilters)
            {
                if (item.wellStatus === filterName) {
                    resultType = true;
                }
            }
        }
        return resultType === true;
    }

    public handleFilterBoxClick = (filterName: string) => (event: any) => {
        const filterIndex = this.state.statusFilters.findIndex((value: any) => value === filterName);
        let listFilters: any[] = this.state.statusFilters;
        if (filterName === 'all') {
            listFilters = [];
        } else if (filterIndex < 0) {
            listFilters.push(filterName);
        } else {
            listFilters.splice(filterIndex, 1);
        }
        if (this.UserStore.hasPermission('Permissions.Wells.ViewArchive')) {
            if (listFilters.length === 4) {
                listFilters = [];
            }
        } else {
            if (listFilters.length === 3) {
                listFilters = [];
            }
        }
        this.setState({statusFilters: listFilters},
            () => this.formatWellListObject());
    }

    public buttonColor = (buttonName: string) => {
        const currList: any[] = this.state.statusFilters;
        if (currList.some(value => value === buttonName)) {
            return 'primary';
        }
        return 'outline-primary';
    }

    public allButtonTypeColor = () => {
        if (this.state.statusFilters.length > 0) {
            return 'outline-primary';
        }
        return 'primary';
    }

    private renderLastTestLink = (well: IWell) => () => 
        well.latestTestId ?
        <Link to={"/well/" + well.id + "/test/" + well.latestTestId + "/welldata/testsetup"} key={well.latestTestId}>
            {moment.unix(Number(well.latestTestDate)).tz(well.timeZoneName).format("MMM DD YYYY, h:mm a")}
        </Link>
        :
        ''

    private formatWellListObject() {
        const newWellList: any[] = [];

        const tableHeaders = [
            {
                'class': 'align-center',
                'content': 'New Report',
                'width': '5'
            },
            'Well Name',
            'Current Test',
            'Operator',
            'Contractor',
            {
                'class': 'align-center',
                'content': 'Rig Number',
                'width': '5'
            },
            {
                'class': 'align-center',
                'content': 'Spud Date',
                'width': '5'
            },
            {
                'class': 'align-center',
                'content': 'Invoice Ready',
                'width': '5'
            },
            {
                'class': 'align-center',
                'content': 'Setup Complete',
                'width': '5'
            },
            {
                'class': 'align-center',
                'content': 'Well Files',
                'width': '5'
            },
            {
                'class': 'align-center',
                'content': 'Dispatches',
                'width': '5'
            }
        ];

        const wells = this.dataService.sort(this.state.wells, {'wellName': 'asc'});

        wells.filter(this.filterNotAssigned).filter(this.filterList).map((well: IWell, index: number) => {
            const dateArray = well.spudDate ? well.spudDate.split('-') : [];
            let sortDate;
            if (dateArray.length === 3) {
                sortDate = new Date(Number(dateArray[2]), Number(dateArray[0]) - 1, Number(dateArray[1]));
            }
            const newObject = [
                {
                    'class': 'align-center',
                    content: this.renderNewTestButton(well),
                    type: 'render',
                },
                {
                    content: this.renderWellName(well),
                    sortItem: well.wellName,
                    type: 'render'
                },
                {
                    content: this.renderLastTestLink(well),
                    sortItem: Number(well.latestTestDate),
                    type: 'render'
                },
                {
                    content: this.renderWellOperator(well),
                    sortItem: well.operatorName,
                    type: 'render'
                },
                {
                    content: this.renderWellContractor(well),
                    sortItem: well.contractorName,
                    type: 'render'
                },
                {
                    content: well.rigNumber,
                },
                {
                    content: this.renderWellSpudDate(well),
                    sortItem: sortDate ? sortDate.valueOf() : Number.MAX_SAFE_INTEGER,
                    type: 'render'
                },
                {
                    content: well.invoiceReady,
                },
                {
                    'class': 'align-center',
                    content: this.renderCompleteIcon(well),
                    sortItem: well.wellSetupComplete,
                    type: 'render',
                },
                {
                    'class': 'align-center',
                    content: this.renderWellFilesButton(well),
                    type: 'render',
                },
                {
                    'class': 'align-center',
                    content: this.renderDispatchesButton(well),
                    type: 'render',
                }
            ];

            newWellList.push(newObject);
        });

        const listStructure = {
            headers: tableHeaders,
            rows: newWellList
        };

        this.setState({
            wellList: listStructure
        });

    }

    private toggleShowAll = (event:any) => {
        const showAll = !this.state.showAll;
        this.setState({
            showAll,
        }, () => this.formatWellListObject());
    }

    private showAllToggleButton = () => {
        if (this.UserStore.hasPermission('Permissions.Wells.Read.Separate')) {
            return <ButtonGroup>
                    <Button type="button" color={this.state.showAll ? "primary" : "outline-primary"} onClick={this.toggleShowAll}>All</Button>
                    <Button type="button" color={this.state.showAll ? "outline-primary" : "primary"} onClick={this.toggleShowAll}>Assigned</Button>
                </ButtonGroup>;
        }
        return '';
    }

    private showStatusFilters = () => {
        if (this.UserStore.hasGenericPermission('Permissions.Wells.ReadClosed')) {
            return (
            <span style={{marginBottom: '1rem', marginLeft: '1rem'}}>
                <Button type='button' color={this.allButtonTypeColor()} onClick={this.handleFilterBoxClick('all')}>All</Button>
                    {' '}
                <ButtonGroup>
                    <Button type='button' color={this.buttonColor('Active')} onClick={this.handleFilterBoxClick('Active')}>Active</Button>
                    <Button type='button' color={this.buttonColor('Finishing')} onClick={this.handleFilterBoxClick('Finishing')}>Finishing</Button>
                    <Button type='button' color={this.buttonColor('Closed')} onClick={this.handleFilterBoxClick('Closed')}>Closed</Button>
                    {
                        this.UserStore.hasPermission('Permissions.Wells.ViewArchive') &&
                        <Button type='button' color={this.buttonColor('Archived')} onClick={this.handleFilterBoxClick('Archived')}>Archived</Button>
                    }
                </ButtonGroup>
            </span>

            );
        }
        return '';
    }
    public render() {
        return(
            <div className="WellList">
                <div className="row header">
                    <div className="col-6">
                        <h1 className="float-left">Well List</h1>
                    </div>
                    <div className="col-6">
                        {
                            this.showAllToggleButton()
                        }
                        {
                            this.showStatusFilters()
                        }
                        {
                            this.state.newWellAvail &&
                            <Link to="/well/new/details">
                                <Button color="primary" className="float-right">
                                    Add New Well
                                </Button>
                            </Link>
                        }
                    </div>
                </div>
                <DataTable loading={this.state.loading} list={this.state.wellList} />
            </div>
        );
    }
}

export default WellList;



// WEBPACK FOOTER //
// ./src/Components/Main/WellList/WellList.tsx